import type { Router } from "vue-router";

import { isNativePlatform } from "../capacitor/platform";
import { clearSessionToken } from "../capacitor/session-token";

import instance from "./api";

// The interceptor only needs to be able to tear down the cached session. Kept
// structural so it accepts either the admin or the user (auth) Pinia store.
// markSessionSlotUnavailable is user-only (the admin store has no slot concept),
// so it is optional and called defensively.
export interface SessionStore {
  resetAndClearPersisted: () => void;
  markSessionSlotUnavailable?: () => void;
}

type RouteType = "admin" | "user" | "unknown";

const AUTH_ROUTES: Record<"totp" | "totpVerify" | "login", Record<RouteType, string | null>> = {
  totp: {
    admin: "AdminTwoFactorOnboard",
    user: "TwoFactorOnboard",
    unknown: null,
  },
  totpVerify: {
    admin: "AdminTwoFactor",
    user: "TwoFactor",
    unknown: null,
  },
  login: {
    admin: "AdminLogin",
    user: "Login",
    unknown: null,
  },
} as const;

function getRouteType(router: Router): RouteType {
  const route = router.currentRoute.value;

  if (route.meta?.requiresAdminLogin) {
    return "admin";
  } else if (route.meta?.requiresUserLogin) {
    return "user";
  }
  return "unknown";
}

// Guards against the interceptor re-entering navigation while an auth redirect
// is already navigating. Without it, a 401 that fires during the redirect (a
// route guard re-probing /status, or a background poll) would push the same
// login route again, which cancels and restarts the in-flight navigation. That
// re-runs the guard probe, 401s again, and loops.
let authRedirectInFlight = false;

async function redirectAndReject(
  router: Router,
  routeName: string | null,
  error: any,
  reason: string,
  sessionExpired = false,
) {
  if (routeName) {
    if (authRedirectInFlight) {
      // A redirect to an auth route is already navigating. Treat this 401 as
      // handled but do not push again; re-pushing restarts that navigation.
      return Promise.reject({ handled: true, reason });
    }
    authRedirectInFlight = true;
    try {
      const query = sessionExpired ? { sessionExpired: "1" } : undefined;
      await router.push({ name: routeName, query });
    } finally {
      authRedirectInFlight = false;
    }
    return Promise.reject({ handled: true, reason });
  }
  return Promise.reject(error);
}

// Attaches the response-policy interceptor to the shared axios instance.
// Owns navigation and session teardown, so it needs the router and the portal's
// session store. Imported only by the entrypoints (main-admin / main-user) to
// keep api.ts a dependency-free leaf and avoid a store <-> api circular import.
export function installResponseInterceptors(router: Router, sessionStore: SessionStore) {
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (error.response) {
        const routeType = getRouteType(router);

        if (error.response.status === 401) {
          const errCode = error.response.data?.Err;

          if (errCode === "err.invalidSession" || errCode === "err.unauthorised") {
            // Invalidate the cached user before redirecting. Without this the
            // route guard trusts the stale cached user and bounces an already
            // "logged in" admin/user back into the protected area, where every
            // request 401s again, looping until the cache ages out.
            sessionStore.resetAndClearPersisted();
            await clearSessionToken();
            return redirectAndReject(
              router,
              AUTH_ROUTES.login[routeType],
              error,
              "invalidSession",
              routeType === "user",
            );
          }
          if (errCode === "err.totpEnrolmentRequired") {
            return redirectAndReject(router, AUTH_ROUTES.totp[routeType], error, "totpEnrolmentRequired");
          }
          if (errCode === "err.totpVerificationRequired") {
            return redirectAndReject(router, AUTH_ROUTES.totpVerify[routeType], error, "totpVerificationRequired");
          }
        }

        // The session holds no session slot: keep the user on the
        // session-limit screen where they free a slot and continue.
        if (
          error.response.status === 409 &&
          error.response.data?.Err === "err.sessionSlotRequired" &&
          routeType === "user" &&
          router.currentRoute.value.name !== "SessionLimit"
        ) {
          // Sync the cached user first: the route guard keeps a session on
          // SessionLimit only while HasSessionSlot === false. Without this the
          // guard trusts a stale "true"/undefined and bounces straight back,
          // so the redirect silently fails.
          sessionStore.markSessionSlotUnavailable?.();
          return redirectAndReject(router, "SessionLimit", error, "sessionSlotRequired");
        }

        // Database is in read-only recovery mode: navigate to the
        // server-rendered recovery page. A dedicated path avoids reloading
        // the cached SPA, which would loop back here.
        if (error.response.status === 503 && error.response.data?.Err === "err.databaseReadOnly") {
          if (isNativePlatform()) {
            // The recovery page is server-rendered and doesn't exist in the
            // local bundle. Redirect to login which will show errors normally.
            router.push({ name: "Login" });
            return new Promise(() => {});
          }
          window.location.href = "/_recovery";
          return new Promise(() => {}); // never resolves; page is navigating
        }
      }

      return Promise.reject(error);
    },
  );
}
